import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'vfx_partner_token';

const Ctx = createContext(null);

export function PartnerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const authHeader = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const applyToken = useCallback((t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    setToken(t || null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/partner/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPartner(res.data.partner);
      } catch {
        if (!cancelled) applyToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/partner/auth/login`, { email, password });
    applyToken(res.data.token);
    setPartner(res.data.partner);
    return res.data.partner;
  };

  const signup = async (payload) => {
    const res = await axios.post(`${API}/partner/auth/signup`, payload);
    applyToken(res.data.token);
    setPartner(res.data.partner);
    return res.data.partner;
  };

  const refresh = async () => {
    if (!token) return null;
    const res = await axios.get(`${API}/partner/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setPartner(res.data.partner);
    return res.data.partner;
  };

  const logout = () => {
    applyToken(null);
    setPartner(null);
  };

  return (
    <Ctx.Provider value={{ partner, token, loading, login, signup, logout, refresh, authHeader, API }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePartnerAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('usePartnerAuth must be used inside PartnerAuthProvider');
  return c;
}
